import { create } from 'zustand'
import { surveyApi, photoApi, convertToJPEG } from '../services/apiService'
import { savePhotoLocally, uploadToR2, syncPhotosToR2, getPendingCount, cacheRemotePhoto } from '../services/storageService'
import { surveyDb } from '../db/database'
import { SURVEY_STATUS } from '../utils/constants'

// Worker API base URL for photo URLs
const WORKER_URL = 'https://clystcore-api.js-55b.workers.dev'

const useSurveyStore = create((set, get) => ({
  surveys: [],
  currentSurvey: null,
  currentPhotos: [],
  currentAiResponses: [],
  loading: false,
  error: null,
  // Offline sync status
  pendingUploads: 0,
  pendingSurveys: 0,
  syncing: false,
  syncingSurveys: false,
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,

  // Load all surveys - offline-first approach
  // Loads from local DB first, then merges with remote if online
  loadSurveys: async () => {
    set({ loading: true, error: null })
    try {
      // Step 1: Load from local DB first (instant)
      const localSurveys = await surveyDb.getAll()

      // Show local surveys immediately
      if (localSurveys.length > 0) {
        set({ surveys: localSurveys })
      }

      // Step 2: If online, fetch from remote and merge
      const { isOnline } = get()
      if (isOnline) {
        try {
          const remoteSurveys = await surveyApi.list()

          // Merge: remote surveys + unsynced local surveys
          const unsyncedLocal = localSurveys.filter(s => !s.synced)

          // Update local DB with remote surveys (mark as synced)
          for (const remote of remoteSurveys) {
            const existing = await surveyDb.getByRemoteId(remote.id)
            if (!existing) {
              // New survey from remote - add to local DB
              await surveyDb.create({
                ...remote,
                synced: true,
                remoteId: remote.id
              })
            }
          }

          // Combine remote surveys with unsynced local ones
          const mergedSurveys = [
            ...unsyncedLocal,
            ...remoteSurveys.map(s => ({ ...s, synced: true, remoteId: s.id }))
          ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

          set({ surveys: mergedSurveys, loading: false })
        } catch (remoteError) {
          // Remote failed, but we have local data - just use that
          console.warn('Remote fetch failed, using local data:', remoteError)
          set({ loading: false })
        }
      } else {
        // Offline - just use local surveys
        set({ loading: false })
      }

      // Update pending count
      const pendingSurveys = await surveyDb.getPendingCount()
      set({ pendingSurveys })
    } catch (error) {
      set({ error: error.message, loading: false })
      console.error('Failed to load surveys:', error)
    }
  },

  // Load a single survey with photos - offline-first approach
  loadSurvey: async (id) => {
    set({ loading: true, error: null })
    try {
      // Check if this is a local-only survey (not yet synced)
      const localSurvey = await surveyDb.getById(id)

      if (localSurvey && !localSurvey.synced) {
        // Unsynced local survey - load from IndexedDB
        const localData = await surveyDb.getWithPhotos(id)
        const localPhotos = (localData.photos || []).map(p => ({
          ...p,
          imageUrl: p.imageData, // Use base64 for display
          thumbnailUrl: p.thumbnailData
        }))
        set({
          currentSurvey: localData,
          currentPhotos: localPhotos,
          currentAiResponses: [],
          loading: false
        })
        return
      }

      // Try to load from remote
      const { isOnline } = get()
      const remoteId = localSurvey?.remoteId || id

      if (isOnline) {
        try {
          const data = await surveyApi.get(remoteId)
          if (data && data.survey) {
            // Map photos to include full URLs
            const photos = (data.photos || []).map(p => ({
              ...p,
              imageUrl: `${WORKER_URL}${p.imageUrl}`,
              thumbnailUrl: p.thumbnailUrl ? `${WORKER_URL}${p.thumbnailUrl}` : null
            }))

            // Set survey data immediately (photos will be cached in background)
            set({
              currentSurvey: { ...data.survey, synced: true, remoteId: data.survey.id },
              currentPhotos: photos,
              currentAiResponses: data.aiResponses || [],
              loading: false
            })

            // Cache photos locally in background (for offline PDF generation)
            // Use the local survey ID for storage
            const localSurveyId = localSurvey?.id || id
            Promise.all(
              photos.map(photo => cacheRemotePhoto(photo, localSurveyId))
            ).then(cachedPhotos => {
              // Update photos in state with cached imageData
              set(state => ({
                currentPhotos: state.currentPhotos.map(p => {
                  const cached = cachedPhotos.find(cp => cp.id === p.id)
                  return cached ? { ...p, imageData: cached.imageData } : p
                })
              }))
            }).catch(err => {
              console.warn('Background photo caching failed:', err)
            })

            return
          }
        } catch (remoteError) {
          console.warn('Remote load failed:', remoteError)
        }
      }

      // Fallback to local if we have it
      if (localSurvey) {
        const localData = await surveyDb.getWithPhotos(id)
        const localPhotos = (localData.photos || []).map(p => ({
          ...p,
          imageUrl: p.imageData,
          thumbnailUrl: p.thumbnailData
        }))
        set({
          currentSurvey: localData,
          currentPhotos: localPhotos,
          currentAiResponses: [],
          loading: false
        })
      } else {
        set({ currentSurvey: null, currentPhotos: [], currentAiResponses: [], loading: false })
      }
    } catch (error) {
      set({ error: error.message, loading: false })
      console.error('Failed to load survey:', error)
    }
  },

  // Create a new survey - offline-first approach
  // Saves locally first, then syncs to remote in background
  createSurvey: async (surveyData) => {
    set({ loading: true, error: null })
    try {
      // Step 1: Save to local DB first (works offline)
      const localSurvey = await surveyDb.create(surveyData)

      // Add to UI immediately
      set(state => ({
        surveys: [localSurvey, ...state.surveys],
        currentSurvey: localSurvey,
        currentPhotos: [],
        pendingSurveys: state.pendingSurveys + 1,
        loading: false
      }))

      // Step 2: Try to sync to remote in background if online
      const { isOnline } = get()
      if (isOnline) {
        try {
          const remoteSurvey = await surveyApi.create(surveyData)

          // Update local DB with remote ID
          await surveyDb.markSynced(localSurvey.id, remoteSurvey.id)

          // Update state with synced survey
          const syncedSurvey = { ...localSurvey, synced: true, remoteId: remoteSurvey.id }
          set(state => ({
            surveys: state.surveys.map(s => s.id === localSurvey.id ? syncedSurvey : s),
            currentSurvey: state.currentSurvey?.id === localSurvey.id ? syncedSurvey : state.currentSurvey,
            pendingSurveys: Math.max(0, state.pendingSurveys - 1)
          }))

          return syncedSurvey
        } catch (syncError) {
          console.warn('Background sync failed, will retry later:', syncError)
          // Survey remains in local storage for later sync
        }
      }

      return localSurvey
    } catch (error) {
      set({ error: error.message, loading: false })
      console.error('Failed to create survey:', error)
      throw error
    }
  },

  // Update survey - offline-first approach
  updateSurvey: async (id, updates) => {
    set({ loading: true, error: null })
    try {
      // Get current survey to check sync status
      const { currentSurvey } = get()
      const localSurvey = await surveyDb.getById(id)

      // Step 1: Update locally first
      const updatedLocal = await surveyDb.update(id, updates)

      // Update UI immediately
      set(state => ({
        surveys: state.surveys.map(s => s.id === id ? updatedLocal : s),
        currentSurvey: state.currentSurvey?.id === id ? updatedLocal : state.currentSurvey,
        loading: false
      }))

      // Step 2: Try to sync to remote if online and survey has been synced before
      const { isOnline } = get()
      const remoteId = localSurvey?.remoteId || currentSurvey?.remoteId

      if (isOnline && remoteId) {
        try {
          const remoteSurvey = await surveyApi.update(remoteId, updates)

          // Mark as synced
          await surveyDb.markSynced(id, remoteId)

          const syncedSurvey = { ...updatedLocal, synced: true, remoteId }
          set(state => ({
            surveys: state.surveys.map(s => s.id === id ? syncedSurvey : s),
            currentSurvey: state.currentSurvey?.id === id ? syncedSurvey : state.currentSurvey
          }))

          return syncedSurvey
        } catch (syncError) {
          console.warn('Background sync failed for update:', syncError)
          // Update pending count
          const pendingSurveys = await surveyDb.getPendingCount()
          set({ pendingSurveys })
        }
      } else if (!remoteId) {
        // Survey hasn't been synced yet - update pending count
        const pendingSurveys = await surveyDb.getPendingCount()
        set({ pendingSurveys })
      }

      return updatedLocal
    } catch (error) {
      set({ error: error.message, loading: false })
      console.error('Failed to update survey:', error)
      throw error
    }
  },

  // Delete a survey via Worker API
  deleteSurvey: async (id) => {
    set({ loading: true, error: null })
    try {
      await surveyApi.delete(id)
      set(state => ({
        surveys: state.surveys.filter(s => s.id !== id),
        currentSurvey: state.currentSurvey?.id === id ? null : state.currentSurvey,
        currentPhotos: state.currentSurvey?.id === id ? [] : state.currentPhotos,
        loading: false
      }))
    } catch (error) {
      set({ error: error.message, loading: false })
      console.error('Failed to delete survey:', error)
      throw error
    }
  },

  // Add photo to current survey - offline-first approach
  // Saves locally first, then uploads to R2 in background
  addPhoto: async (photoData) => {
    const { currentSurvey, isOnline } = get()
    if (!currentSurvey) {
      throw new Error('No survey selected')
    }

    set({ loading: true, error: null })
    try {
      // Convert image to JPEG for PDF compatibility
      let imageBlob = photoData.blob
      if (imageBlob && imageBlob.type !== 'image/jpeg') {
        imageBlob = await convertToJPEG(imageBlob)
      }

      // Create thumbnail (smaller version)
      let thumbnailBlob = null
      if (imageBlob) {
        thumbnailBlob = await createThumbnail(imageBlob, 300)
      }

      // Step 1: Save locally first (offline-first)
      const localPhoto = await savePhotoLocally({
        surveyId: currentSurvey.id,
        categoryCode: photoData.categoryCode,
        blob: imageBlob,
        thumbnailBlob,
        notes: photoData.notes || ''
      })

      // Create photo object for UI with local data URL
      const photoForUI = {
        id: localPhoto.id,
        localId: localPhoto.id,
        surveyId: currentSurvey.id,
        categoryCode: photoData.categoryCode,
        imageUrl: localPhoto.imageData, // Use base64 for immediate display
        thumbnailUrl: localPhoto.thumbnailData,
        notes: localPhoto.notes,
        analyseWithAI: localPhoto.analyseWithAI,
        uploaded: false,
        capturedAt: localPhoto.capturedAt
      }

      // Add to UI immediately
      set(state => ({
        currentPhotos: [...state.currentPhotos, photoForUI],
        pendingUploads: state.pendingUploads + 1,
        loading: false
      }))

      // Step 2: Upload to R2 in background if online
      if (isOnline) {
        try {
          const result = await uploadToR2(localPhoto)
          if (result.success) {
            // Update photo in state with remote URLs
            set(state => ({
              currentPhotos: state.currentPhotos.map(p =>
                p.localId === localPhoto.id
                  ? {
                      ...p,
                      id: result.remotePhoto.id,
                      remoteId: result.remotePhoto.id,
                      imageUrl: `${WORKER_URL}/api/photos/${result.remotePhoto.id}/image`,
                      thumbnailUrl: `${WORKER_URL}/api/photos/${result.remotePhoto.id}/thumbnail`,
                      uploaded: true
                    }
                  : p
              ),
              pendingUploads: Math.max(0, state.pendingUploads - 1)
            }))
          }
        } catch (uploadError) {
          console.warn('Background upload failed, will retry later:', uploadError)
          // Photo remains in local storage for later sync
        }
      }

      return photoForUI
    } catch (error) {
      set({ error: error.message, loading: false })
      console.error('Failed to add photo:', error)
      throw error
    }
  },

  // Sync all pending photos to R2
  syncPendingPhotos: async (forceReset = false) => {
    const { isOnline, syncing } = get()
    if (!isOnline) return { success: false, message: 'Offline' }
    if (syncing && !forceReset) return { success: false, message: 'Already syncing' }

    set({ syncing: true })
    try {
      const result = await syncPhotosToR2((current, total) => {
        console.log(`Syncing photo ${current}/${total}`)
      }, forceReset)

      // Always get actual count from DB after sync (source of truth)
      const actualPending = await getPendingCount()
      set({ pendingUploads: actualPending, syncing: false })

      return result
    } catch (error) {
      // Still refresh from DB on error
      const actualPending = await getPendingCount()
      set({ pendingUploads: actualPending, syncing: false })
      console.error('Sync failed:', error)
      return { success: false, error: error.message }
    }
  },

  // Sync all pending surveys to remote
  syncPendingSurveys: async () => {
    const { isOnline, syncingSurveys } = get()
    if (!isOnline) return { success: false, message: 'Offline' }
    if (syncingSurveys) return { success: false, message: 'Already syncing surveys' }

    set({ syncingSurveys: true })
    try {
      const pendingSurveys = await surveyDb.getPending()

      if (pendingSurveys.length === 0) {
        set({ syncingSurveys: false, pendingSurveys: 0 })
        return { success: true, synced: 0, message: 'All surveys already synced' }
      }

      console.log(`Syncing ${pendingSurveys.length} pending surveys...`)

      let synced = 0
      let failed = 0
      const errors = []

      for (const survey of pendingSurveys) {
        try {
          if (survey.remoteId) {
            // Survey exists remotely - update it
            await surveyApi.update(survey.remoteId, {
              propertyName: survey.propertyName,
              propertyAddress: survey.propertyAddress,
              propertyPostcode: survey.propertyPostcode,
              clientName: survey.clientName,
              surveyDate: survey.surveyDate,
              surveyorName: survey.surveyorName,
              surveyLevel: survey.surveyLevel,
              status: survey.status,
              reviewData: survey.reviewData,
              analysisResult: survey.analysisResult
            })
            await surveyDb.markSynced(survey.id, survey.remoteId)
          } else {
            // New survey - create on remote
            const remoteSurvey = await surveyApi.create({
              propertyName: survey.propertyName,
              propertyAddress: survey.propertyAddress,
              propertyPostcode: survey.propertyPostcode,
              clientName: survey.clientName,
              surveyDate: survey.surveyDate,
              surveyorName: survey.surveyorName,
              surveyLevel: survey.surveyLevel
            })
            await surveyDb.markSynced(survey.id, remoteSurvey.id)
          }
          synced++
        } catch (err) {
          failed++
          errors.push({ surveyId: survey.id, error: err.message })
          console.error(`Failed to sync survey ${survey.id}:`, err)
        }
      }

      // Refresh pending count
      const remainingPending = await surveyDb.getPendingCount()
      set({ syncingSurveys: false, pendingSurveys: remainingPending })

      // Reload surveys to get updated state
      get().loadSurveys()

      return {
        success: failed === 0,
        synced,
        failed,
        errors,
        message: failed === 0
          ? `Successfully synced ${synced} surveys`
          : `Synced ${synced} surveys, ${failed} failed`
      }
    } catch (error) {
      const remainingPending = await surveyDb.getPendingCount()
      set({ syncingSurveys: false, pendingSurveys: remainingPending })
      console.error('Survey sync failed:', error)
      return { success: false, error: error.message }
    }
  },

  // Update pending count from local DB
  refreshPendingCount: async () => {
    const photoCount = await getPendingCount()
    const surveyCount = await surveyDb.getPendingCount()
    set({ pendingUploads: photoCount, pendingSurveys: surveyCount })
  },

  // Set online status
  setOnlineStatus: (isOnline) => {
    set({ isOnline })
    // Auto-sync when coming back online
    if (isOnline) {
      get().syncPendingSurveys()
      get().syncPendingPhotos()
    }
  },

  // Delete a photo via Worker API
  deletePhoto: async (photoId) => {
    set({ loading: true, error: null })
    try {
      await photoApi.delete(photoId)
      set(state => ({
        currentPhotos: state.currentPhotos.filter(p => p.id !== photoId),
        loading: false
      }))
    } catch (error) {
      set({ error: error.message, loading: false })
      console.error('Failed to delete photo:', error)
      throw error
    }
  },

  // Update photo notes via Worker API
  updatePhotoNotes: async (photoId, notes) => {
    set({ loading: true, error: null })
    try {
      await photoApi.update(photoId, { notes })
      set(state => ({
        currentPhotos: state.currentPhotos.map(p =>
          p.id === photoId ? { ...p, notes } : p
        ),
        loading: false
      }))
    } catch (error) {
      set({ error: error.message, loading: false })
      console.error('Failed to update photo notes:', error)
      throw error
    }
  },

  // Toggle photo AI analysis flag via Worker API
  togglePhotoAnalyse: async (photoId, analyseWithAI) => {
    try {
      await photoApi.update(photoId, { analyseWithAI })
      set(state => ({
        currentPhotos: state.currentPhotos.map(p =>
          p.id === photoId ? { ...p, analyseWithAI } : p
        )
      }))
    } catch (error) {
      console.error('Failed to toggle photo analyse flag:', error)
      throw error
    }
  },

  // Mark survey as completed
  completeSurvey: async () => {
    const { currentSurvey } = get()
    if (!currentSurvey) return

    return get().updateSurvey(currentSurvey.id, {
      status: SURVEY_STATUS.COMPLETED
    })
  },

  // Mark survey as analysed
  markAnalysed: async (analysisResult) => {
    const { currentSurvey } = get()
    if (!currentSurvey) return

    return get().updateSurvey(currentSurvey.id, {
      status: SURVEY_STATUS.ANALYSED,
      analysisResult
    })
  },

  // Get photos by category for current survey
  getPhotosByCategory: (categoryCode) => {
    return get().currentPhotos.filter(p => p.categoryCode === categoryCode)
  },

  // Get photo count by category code
  getPhotoCounts: () => {
    const counts = {}
    get().currentPhotos.forEach(photo => {
      counts[photo.categoryCode] = (counts[photo.categoryCode] || 0) + 1
    })
    return counts
  },

  // Clear current survey
  clearCurrentSurvey: () => {
    set({ currentSurvey: null, currentPhotos: [] })
  },

  // Clear error
  clearError: () => set({ error: null })
}))

// Helper: Create thumbnail from image blob
async function createThumbnail(blob, maxSize = 300) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      let { width, height } = img

      // Scale down to maxSize
      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width
          width = maxSize
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height
          height = maxSize
        }
      }

      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      // Fill with white background for JPEG (no transparency)
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, width, height)
      ctx.drawImage(img, 0, 0, width, height)

      canvas.toBlob(
        (thumbnailBlob) => {
          if (thumbnailBlob) {
            resolve(thumbnailBlob)
          } else {
            reject(new Error('Failed to create thumbnail'))
          }
        },
        'image/jpeg',
        0.8
      )
    }
    img.onerror = () => reject(new Error('Failed to load image for thumbnail'))
    img.src = URL.createObjectURL(blob)
  })
}

export default useSurveyStore
